/**
 * Que pasa cuando el stock no alcanza (CHK-06).
 *
 * Son tres resultados distintos y cada uno merece un mensaje distinto; el error
 * clasico de esta pantalla es tratarlos como uno solo:
 *
 * - `faltante`: falta algo concreto. Se dice QUE falta, CUANTO queda, y se
 *   ofrecen las salidas —ajustar a lo que hay, quitarlo, o preguntar por
 *   WhatsApp—. Sin salidas, el aviso es solo una puerta cerrada.
 * - `random_no_disponible`: el stock no se pudo confirmar. NO es lo mismo que
 *   "no hay": no se bloquea nada, se avisa que la disponibilidad se confirma
 *   antes de despachar (RND-03).
 * - `todo_disponible`: se confirma y no se estorba.
 *
 * La tercera alternativa que pide la historia —"que me avisen cuando vuelva a
 * haber stock"— se resuelve por WhatsApp y no con un aviso automatico por
 * correo: ese aviso exige una suscripcion, un disparador en la sincronizacion y
 * un correo mas, y nada de eso esta comprometido en el alcance. El boton lleva
 * escrito que productos son, asi que quien atiende puede responder cuando
 * llegan sin preguntar nada.
 */
import type { Carro, RespuestaVerificacionStock } from '@elcarpintero/compartido';

import { registrarClicWhatsapp } from '../../servicios/metricas.servicio';
import { textoDisponibles } from '../../utilidades/paquete';
import { whatsappFaltantes } from '../../utilidades/whatsapp';
import { Icono } from '../comunes/Icono';

interface Props {
  verificacion: RespuestaVerificacionStock;
  carro: Carro;
  alAjustar: (sku: string, cantidad: number) => void;
  alQuitar: (sku: string) => void;
}

export function AvisoDisponibilidad({ verificacion, carro, alAjustar, alQuitar }: Props) {
  if (verificacion.resultado === 'todo_disponible') {
    return (
      <p className="ck-alerta" role="status">
        <Icono nombre="check" />
        <span>Stock confirmado para todo tu pedido.</span>
      </p>
    );
  }

  if (verificacion.resultado === 'random_no_disponible') {
    return (
      <p className="ck-alerta" role="status">
        <Icono nombre="info" />
        <span>
          No pudimos confirmar el stock en este momento. Puedes continuar: revisamos la
          disponibilidad antes de despachar y te avisamos si algo cambia.
        </span>
      </p>
    );
  }

  const faltantes = verificacion.items.filter((item) => !item.disponible);
  const productoPorSku = new Map(carro.items.map((item) => [item.producto.sku, item.producto]));

  return (
    <div className="ck-alerta critica" role="alert">
      <Icono nombre="alerta" />

      <div>
        <h2>Nos quedamos sin stock de algo que llevas</h2>
        <p>
          Lo sentimos. El stock cambió mientras armabas tu pedido: ajusta o quita lo que falta para
          seguir, o escribenos y lo vemos contigo.
        </p>

        <ul className="ck-faltantes">
          {faltantes.map((item) => {
            const producto = productoPorSku.get(item.sku);
            const nombre = producto?.nombre ?? item.sku;
            // En paquetes cuando se vende de a paquete (VTA-01): "quedan 2" a
            // secas se lee como dos clavos.
            const cuantos = (cantidad: number): string =>
              producto !== undefined ? textoDisponibles(producto, cantidad) : String(cantidad);

            return (
              <li key={item.sku}>
                <span className="ck-faltante-datos">
                  <span className="n">{nombre}</span>
                  <span className="d">
                    {item.stockDisponible === 0
                      ? `Sin stock · pediste ${cuantos(item.cantidadSolicitada)}`
                      : `Quedan ${cuantos(item.stockDisponible)} · pediste ${cuantos(item.cantidadSolicitada)}`}
                  </span>
                </span>

                <span className="ck-faltante-acciones">
                  {item.stockDisponible > 0 ? (
                    <button
                      type="button"
                      className="btn btn-contorno btn-sm"
                      onClick={() => alAjustar(item.sku, item.stockDisponible)}
                    >
                      Dejar {item.stockDisponible}
                    </button>
                  ) : null}

                  <button type="button" className="btn btn-contorno btn-sm" onClick={() => alQuitar(item.sku)}>
                    Quitar
                  </button>
                </span>
              </li>
            );
          })}
        </ul>

        <p className="ck-acciones">
          <a
            className="btn btn-wa btn-sm"
            href={whatsappFaltantes(faltantes.map((item) => productoPorSku.get(item.sku)?.nombre ?? item.sku))}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => registrarClicWhatsapp({ origen: 'checkout' })}
          >
            <Icono nombre="wa" />
            Preguntar cuándo llega
          </a>
        </p>
      </div>
    </div>
  );
}
